import { useMemo } from 'react';

import { generatePath, useLocation, useParams } from 'react-router-dom';

import { Icon } from '@chakra-ui/react';

import {
  NavSection,
  YellowWarningBold,
  FrameOutline,
  FrameBold,
  Profile2UserOutline,
  Profile2UserBold,
  MessageOutline,
  MessageBold,
  SecuritySafeOutline,
  SecuritySafeBold,
  SettingsOutline,
  SettingsBold,
} from '@aslanpay/portal-common-lib';

import MegaphoneOutline from 'app/assets/icons/MegaphoneOutline';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useFetchKybPage from 'app/hooks/useFetchKybPage/useFetchKybPage';
import useFetchOrdersPage from 'app/hooks/useFetchOrdersPage/useFetchOrdersPage';
import { getSidebarPaths } from 'app/utils/routes';
import { companyPaths } from 'app/constants/routes';
import { useManagingCompanyStore } from 'app/store';

import { AuthenticatedLayoutView } from './types';

const ICON_SIZE = '20px';

// Links that are still prototypes and excluded from the visible nav.
const HIDDEN_LINK_IDS = ['home', 'analytics'];

interface SidebarNav {
  sections: NavSection[];
  activeId: string;
}

/**
 * Maps backoffice's existing nav config into the shared Sidebar's data shape.
 * The "Reward & Recognition" entry becomes a labeled expandable section so its
 * sub-pages (Overview, Prides, The Roar, Campaigns, Approvals, Settings) are
 * reachable from the nav.
 */
export function useSidebarNav(view: AuthenticatedLayoutView): SidebarNav {
  const { pathname } = useLocation();
  const { companyId } = useParams();
  const { company } = useManagingCompanyStore();

  const { data: companyConfiguration } = useFetchCompanyConfiguration(company?.id || '');
  const modules = companyConfiguration?.modulesConfig.modules;
  const isGiftingEnabled = modules?.GIFTING === 'ENABLED';
  const isGiftingOnly =
    !!modules &&
    isGiftingEnabled &&
    Object.entries(modules).every(([key, value]) => key === 'GIFTING' || value === 'DISABLED');

  // Surface a warning indicator on Approvals when there are items awaiting action.
  const { data: pendingKybs } = useFetchKybPage({
    query: '',
    status: ['AWAITING_APPROVAL'],
    page: 0,
    size: 1,
  });
  const { data: pendingOrders } = useFetchOrdersPage({ statuses: ['REQUESTED'], page: 0, size: 1 });
  const hasPendingApprovals = (pendingKybs?.total || 0) > 0 || (pendingOrders?.total || 0) > 0;

  const navLinks = useMemo(() => {
    let links = getSidebarPaths(view, companyId).filter(
      (link) => !HIDDEN_LINK_IDS.includes(link.id)
    );

    if (view === 'company' && !isGiftingEnabled) {
      links = links.filter((link) => link.id !== 'gifting');
    }
    if (view === 'company' && isGiftingOnly) {
      links = links.filter((link) => link.id !== 'pay-calendars');
    }

    return links;
  }, [view, companyId, isGiftingEnabled, isGiftingOnly]);

  const recognitionsBasePath = companyId
    ? generatePath(companyPaths.recognitions.path, { companyId })
    : '';
  const isOnRecognitionPage =
    !!recognitionsBasePath &&
    (pathname === recognitionsBasePath || pathname.startsWith(`${recognitionsBasePath}/`));

  const sections = useMemo<NavSection[]>(() => {
    const mapToNavItem = (link: (typeof navLinks)[number]) => ({
      id: link.id,
      label: link.name,
      path: link.path,
      icon: <Icon as={link.icon} boxSize={ICON_SIZE} />,
      iconActive: <Icon as={link.activeIcon} boxSize={ICON_SIZE} />,
      badge:
        link.id === 'approvals' && hasPendingApprovals ? (
          <Icon as={YellowWarningBold} boxSize={ICON_SIZE} />
        ) : undefined,
    });

    const recognitionsIdx = navLinks.findIndex((l) => l.id === 'recognitions');

    if (recognitionsIdx === -1 || !companyId) {
      return [{ id: 'main', items: navLinks.map(mapToNavItem) }];
    }

    const preLinks = navLinks.slice(0, recognitionsIdx);
    const postLinks = navLinks.slice(recognitionsIdx + 1);

    const recognitionSubItems = [
      {
        id: 'recognitions-overview',
        label: 'Overview',
        path: generatePath(companyPaths.recognitions.path, { companyId }),
        icon: <Icon as={FrameOutline} boxSize={ICON_SIZE} />,
        iconActive: <Icon as={FrameBold} boxSize={ICON_SIZE} />,
      },
      {
        id: 'recognitions-prides',
        label: 'Prides',
        path: generatePath(companyPaths.prides.path, { companyId }),
        icon: <Icon as={Profile2UserOutline} boxSize={ICON_SIZE} />,
        iconActive: <Icon as={Profile2UserBold} boxSize={ICON_SIZE} />,
      },
      {
        id: 'recognitions-the-roar',
        label: 'The Roar',
        path: generatePath(companyPaths.theRoar.path, { companyId }),
        icon: <Icon as={MessageOutline} boxSize={ICON_SIZE} />,
        iconActive: <Icon as={MessageBold} boxSize={ICON_SIZE} />,
      },
      {
        id: 'recognitions-campaigns',
        label: 'Campaigns',
        path: generatePath(companyPaths.campaigns.path, { companyId }),
        icon: <MegaphoneOutline boxSize={ICON_SIZE} />,
        iconActive: <MegaphoneOutline boxSize={ICON_SIZE} />,
      },
      {
        id: 'recognitions-approvals',
        label: 'Approvals',
        path: generatePath(companyPaths.recognitionApprovals.path, { companyId }),
        icon: <Icon as={SecuritySafeOutline} boxSize={ICON_SIZE} />,
        iconActive: <Icon as={SecuritySafeBold} boxSize={ICON_SIZE} />,
      },
      {
        id: 'recognitions-settings',
        label: 'Settings',
        path: generatePath(companyPaths.recognitionSettings.path, { companyId }),
        icon: <Icon as={SettingsOutline} boxSize={ICON_SIZE} />,
        iconActive: <Icon as={SettingsBold} boxSize={ICON_SIZE} />,
      },
    ];

    return [
      ...(preLinks.length > 0 ? [{ id: 'main-top', items: preLinks.map(mapToNavItem) }] : []),
      {
        id: 'recognitions',
        label: 'Reward & Recognition',
        defaultExpanded: isOnRecognitionPage,
        items: recognitionSubItems,
      },
      ...(postLinks.length > 0 ? [{ id: 'main-bottom', items: postLinks.map(mapToNavItem) }] : []),
    ];
  }, [navLinks, hasPendingApprovals, companyId, isOnRecognitionPage]);

  const activeId = useMemo(() => {
    const recognitionSubPaths = companyId
      ? [
          {
            id: 'recognitions-prides',
            path: generatePath(companyPaths.prides.path, { companyId }),
          },
          {
            id: 'recognitions-the-roar',
            path: generatePath(companyPaths.theRoar.path, { companyId }),
          },
          {
            id: 'recognitions-campaigns',
            path: generatePath(companyPaths.campaigns.path, { companyId }),
          },
          {
            id: 'recognitions-approvals',
            path: generatePath(companyPaths.recognitionApprovals.path, { companyId }),
          },
          {
            id: 'recognitions-settings',
            path: generatePath(companyPaths.recognitionSettings.path, { companyId }),
          },
          {
            id: 'recognitions-overview',
            path: generatePath(companyPaths.recognitions.path, { companyId }),
          },
        ]
      : [];

    const allLinks = [
      ...navLinks.filter((l) => l.id !== 'recognitions').map((l) => ({ id: l.id, path: l.path })),
      ...recognitionSubPaths,
    ];

    const match = [...allLinks]
      .sort((a, b) => b.path.length - a.path.length)
      .find((link) => pathname === link.path || pathname.startsWith(`${link.path}/`));

    return match?.id ?? '';
  }, [navLinks, companyId, pathname]);

  return { sections, activeId };
}
